// The encounter-map AOE manager owns the live FT3D placement lifecycle.
import { FT3DAOEPlacement, type FT3DActiveAOE, type Ft3dSummonPlacementRequest, type Ft3dCreaturePlacementRequest, type Ft3dZoneEditRequest } from './FT3DAOEPlacement';
import type { Ft3dAoeTargetPreview } from '../../shared/ft3d-aoe-placement-contract';
import { claimFt3dInteractionMode, releaseFt3dInteractionMode } from '../../shared/ft3d-interaction-mode';
import type { TeleportPlacementRequest } from '../../shared/teleport-placement';

declare global {
    interface Window {
        AOEManager?: typeof AOEManager;
        aoeManager?: AOEManager;
        ftGetTargetSelection?: () => string;
        ftSetTargetSelection?: (selection: string, source?: string) => void;
        ftGetActiveEncounterId?: () => string | null;
        ftGetActiveCasterId?: () => string | null;
        ftAoeActiveAbilityId?: string | null;
        ftActionBarRepaintAll?: () => void;
        ftActionBarSetAoeTargets?: (abilityId: string, targets: readonly Ft3dAoeTargetPreview[]) => void;
        ftActionBarArmAoeChoice?: (button: HTMLElement) => boolean;
        ftActionBarClearChoice?: () => void;
        ftAbRitualArmed?: Set<string>;
        ftAbUpcastLevel?: Map<string, number>;
        ftStartZoneEdit?: (request: Ft3dZoneEditRequest) => boolean;
        ftTryCommitZoneMove?: (request: { encounterId: string; statusId: string; span: { ox: number; oy: number; oz: number; ex: number; ey: number; ez: number }; showLogMessage?: boolean }) => Promise<unknown>;
    }
}

export class AOEManager {
    static #instance: AOEManager | undefined;
    static get instance(): AOEManager {
        if (!AOEManager.#instance) AOEManager.#instance = new AOEManager();
        return AOEManager.#instance;
    }

    ft3dActive: FT3DActiveAOE | null = null;
    _ft3dPlacement: FT3DAOEPlacement;

    constructor() {
        if (AOEManager.#instance) throw new Error('AOEManager is a singleton');
        this._ft3dPlacement = new FT3DAOEPlacement();
    }

    confirmActive(): void {
        if (this.ft3dActive) this.confirmFt3d();
    }

    startTeleport(request: TeleportPlacementRequest): void {
        if (this.ft3dActive) this.cancelFt3d();
        claimFt3dInteractionMode('aoe-placement', () => this.cancelFt3d());
        this._ft3dPlacement.startTeleport(request);
        this.ft3dActive = this._ft3dPlacement.active;
        window.ftActionBarRepaintAll?.();
    }

    /** MV2 S1b: the GM zone edit — see FT3DAOEPlacement.startZoneEdit. */
    startZoneEdit(request: Ft3dZoneEditRequest): boolean {
        if (this.ft3dActive) this.cancelFt3d();
        claimFt3dInteractionMode('aoe-placement', () => this.cancelFt3d());
        const started = this._ft3dPlacement.startZoneEdit(request);
        this.ft3dActive = this._ft3dPlacement.active;
        return started;
    }

    /** GM creature drop from the add-creature picker (owner 2026-09-11) — see FT3DAOEPlacement.startCreature. */
    startCreaturePlacement(request: Ft3dCreaturePlacementRequest): boolean {
        if (this.ft3dActive) this.cancelFt3d();
        claimFt3dInteractionMode('aoe-placement', () => this.cancelFt3d());
        const started = this._ft3dPlacement.startCreature(request);
        this.ft3dActive = this._ft3dPlacement.active;
        return started;
    }

    private holdingAbility(button: HTMLElement, kind: 'aoe' | 'summon'): boolean {
        const intent = this.ft3dActive?.envelope.intent;
        const casterId = kind === 'summon'
            ? button.closest<HTMLElement>('[data-ft-caster-id]')?.dataset.ftCasterId || window.ftGetActiveCasterId?.()
            : window.ftGetActiveCasterId?.();
        return intent?.placementKind === kind
            && intent.abilityId === String(button.dataset.ability_id || '').trim().toUpperCase()
            && intent.casterId === String(casterId || '').trim().toUpperCase()
            && intent.encounterId === String(window.ftGetActiveEncounterId?.() || '').trim().toUpperCase();
    }

    startFt3d(button: HTMLElement): void {
        const rearming = this.holdingAbility(button, 'aoe');
        if (this.ft3dActive && !rearming) this.cancelFt3d();
        claimFt3dInteractionMode('aoe-placement', () => {
            if (this.ft3dActive) this.cancelFt3d();
        });
        const started = this._ft3dPlacement.start(button, rearming);
        this.ft3dActive = this._ft3dPlacement.active;
        if (!started || !this.ft3dActive) { this.cancelFt3d(); return; }
        if (!rearming) {
            const id = button.dataset.ability_id || '';
            const ability = (window as Window & { ftFindFullNestedItem?: (id: string) => unknown }).ftFindFullNestedItem?.(id);
            window.ftAbilityHumStart?.(id, ability ?? null);
        }
        // The Action Bar reads THIS mirror; the placement's own repaint ran before it was in sync.
        window.ftActionBarRepaintAll?.();
    }

    startFt3dSummon(button: HTMLElement, request: Ft3dSummonPlacementRequest): void {
        const rearming = this.holdingAbility(button, 'summon');
        if (this.ft3dActive && !rearming) this.cancelFt3d();
        claimFt3dInteractionMode('aoe-placement', () => {
            if (this.ft3dActive) this.cancelFt3d();
        });
        if (!rearming) window.ftAbilityHumStart?.(button?.getAttribute('data-ability_id') || '', null);
        this._ft3dPlacement.startSummon(button, request, rearming);
        this.ft3dActive = this._ft3dPlacement.active;
        window.ftActionBarRepaintAll?.();
    }

    stageFt3dPlacement(payload: unknown): void {
        this._ft3dPlacement.stage(payload);
        this.ft3dActive = this._ft3dPlacement.active;
        window.ftActionBarRepaintAll?.();
    }

    addFt3dProjectile(removeLast = false): void {
        this._ft3dPlacement.addProjectile(removeLast);
        this.ft3dActive = this._ft3dPlacement.active;
        window.ftActionBarRepaintAll?.();
    }

    /** A rolled-count summon placement: cast with the spots banked so far (FT3DAOEPlacement.castSummonsNow). */
    castFt3dSummonsNow(): void {
        this._ft3dPlacement.castSummonsNow();
        this.ft3dActive = this._ft3dPlacement.active;
        window.ftActionBarRepaintAll?.();
        if (!this.ft3dActive) {
            window.ftAbilityHumStop?.();
            releaseFt3dInteractionMode('aoe-placement');
        }
    }

    confirmFt3d(): void {
        this._ft3dPlacement.confirm();
        this.ft3dActive = this._ft3dPlacement.active;
        // The placement's own repaint ran while this mirror still held the old intent, so the Action Bar
        // placement row would linger after the cast. Repaint once more with the mirror in sync.
        window.ftActionBarRepaintAll?.();
        if (!this.ft3dActive) {
            window.ftAbilityHumStop?.();
            releaseFt3dInteractionMode('aoe-placement');
        }
    }

    cancelFt3d(): void {
        window.ftAbilityHumStop?.();
        this._ft3dPlacement.cancel();
        this.ft3dActive = null;
        window.ftActionBarRepaintAll?.();
        releaseFt3dInteractionMode('aoe-placement');
    }

    setUpcastLevel(abilityId: string, level: number): void {
        if (!this.ft3dActive) return;
        this._ft3dPlacement.setUpcastLevel(abilityId, level);
        this.ft3dActive = this._ft3dPlacement.active;
    }
}

// --------------------------------------------------------------
// Export singleton + attach to window (preserves pre-Phase-2 API).
// --------------------------------------------------------------
const aoeManager = AOEManager.instance;
Object.assign(window, { AOEManager, aoeManager });
window.ftStartTeleportPlacement = request => aoeManager.startTeleport(request);
window.ftStartCreaturePlacement = request => aoeManager.startCreaturePlacement(request);
window.ftStartZoneEdit = request => aoeManager.startZoneEdit(request);
